/**
 * هوية المنصة العامة (نصوص صفحة الدخول والسمة الافتراضية).
 * تُقرأ عبر bootstrap_status وتُخزَّن مؤقتًا في الذاكرة لتفادي تكرار الطلب.
 */

import { useEffect, useState } from 'react';
import { useAuth } from './auth';
import { useTheme } from './theme';
import { DEFAULT_PLATFORM_BRANDING, type PlatformBranding } from '@/data/types';
import type { ThemeTokens } from '@/domain/types';

interface PlatformBrandingState {
  branding: PlatformBranding;
  theme: ThemeTokens | null;
  loading: boolean;
}

type PlatformBrandingPayload = { branding: PlatformBranding; theme: ThemeTokens | null };
type PlatformBrandingListener = (payload: PlatformBrandingPayload) => void;

let cache: PlatformBrandingPayload | null = null;
let inflight: Promise<PlatformBrandingPayload> | null = null;
const listeners = new Set<PlatformBrandingListener>();

export function invalidatePlatformBrandingCache(): void {
  cache = null;
  inflight = null;
}

/**
 * يحدّث الذاكرة المؤقتة ويبلّغ كل من يعرض هوية المنصة (مثل هيكل المنصة)
 * بعد الحفظ، دون الحاجة إلى إعادة تحميل الصفحة.
 */
export function publishPlatformBranding(payload: PlatformBrandingPayload): void {
  cache = payload;
  for (const listener of listeners) listener(payload);
}

export function usePlatformBranding(): PlatformBrandingState {
  const { gateway } = useAuth();
  const { applyBranding } = useTheme();
  const [state, setState] = useState<PlatformBrandingState>(() =>
    cache
      ? { ...cache, loading: false }
      : { branding: DEFAULT_PLATFORM_BRANDING, theme: null, loading: true },
  );

  useEffect(() => {
    let active = true;
    if (cache) {
      setState({ ...cache, loading: false });
    } else {
      if (!inflight) {
        inflight = gateway.commands.bootstrapStatus().then((status) => {
          const result = { branding: status.branding, theme: status.theme };
          cache = result;
          return result;
        });
      }
      void inflight
        .then((result) => {
          if (active) setState({ ...result, loading: false });
        })
        .catch(() => {
          inflight = null;
          if (active) setState((current) => ({ ...current, loading: false }));
        });
    }
    return () => {
      active = false;
    };
  }, [gateway]);

  useEffect(() => {
    const listener: PlatformBrandingListener = (payload) => {
      setState({ ...payload, loading: false });
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (state.theme) applyBranding(state.theme, undefined, 'platform');
  }, [applyBranding, state.theme]);

  useEffect(() => {
    const name = state.branding.productName.trim();
    if (!state.loading && name) document.title = name;
  }, [state.branding.productName, state.loading]);

  return state;
}
